import React, { useState } from 'react';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { apiRequest } from '../utils/apiUtils';
import { useAuth } from '../contexts/AuthContext';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage?: string;
  currentUrl?: string;
}

type FeedbackType = 'bug' | 'feature' | 'improvement' | 'other';

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, currentPage, currentUrl }) => {
  const { isMobile } = useResponsive();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    type: 'bug' as FeedbackType,
    description: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'La descripción debe tener al menos 10 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    const screenInfo = `${window.screen.width}x${window.screen.height}`;
    const platform = navigator.platform;
    const language = navigator.language;
    
    return {
      userAgent,
      screen: screenInfo,
      platform,
      language,
      url: currentUrl || window.location.href,
      page: currentPage || 'Página desconocida',
      timestamp: new Date().toISOString()
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const deviceInfo = getDeviceInfo();
      
      const feedbackData = {
        type: formData.type,
        description: formData.description,
        userId: user?.id,
        userEmail: user?.email,
        userName: user?.name,
        deviceInfo: JSON.stringify(deviceInfo),
        submittedAt: new Date().toISOString()
      };

      const response = await apiRequest('/api/feedback', {
        method: 'POST',
        body: feedbackData
      });

      if (response.error) {
        throw new Error(response.error);
      }

      setSubmitSuccess(true);
      
      // Resetear formulario después de 2 segundos
      setTimeout(() => {
        setFormData({
          type: 'bug',
          description: ''
        });
        setSubmitSuccess(false);
        onClose();
      }, 2000);

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting feedback:', error);
      setErrors({ submit: 'Error al enviar el feedback. Inténtalo de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        type: 'bug',
        description: ''
      });
      setErrors({});
      setSubmitSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  if (submitSuccess) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? '20px' : '40px'
      }}>
        <div style={{
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          padding: theme.spacing.lg,
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center',
          border: `2px solid #FF6B35`
        }}>
          <div style={{ fontSize: '48px', marginBottom: theme.spacing.md }}>✅</div>
          <h3 style={{
            color: '#FF6B35',
            marginBottom: theme.spacing.sm,
            fontFamily: theme.fonts.heading
          }}>
            ¡Feedback Enviado!
          </h3>
          <p style={{
            color: theme.colors.textSecondary,
            fontFamily: theme.fonts.body
          }}>
            Gracias por tu reporte. Lo revisaremos pronto.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '10px' : '20px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: theme.spacing.lg,
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: `2px solid #FF6B35`
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg,
          paddingBottom: theme.spacing.md,
          borderBottom: `1px solid ${theme.colors.border}`
        }}>
          <h2 style={{
            color: '#FF6B35',
            fontFamily: theme.fonts.heading,
            fontSize: theme.fonts.sizeTitle,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm
          }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            Reportar Problema
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textSecondary,
              fontSize: '24px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              padding: '4px',
              borderRadius: '4px',
              opacity: isSubmitting ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Información de la página */}
          {currentPage && (
            <div style={{ 
              marginBottom: theme.spacing.md,
              padding: theme.spacing.sm,
              backgroundColor: theme.colors.background,
              borderRadius: theme.borderRadius.input,
              border: `1px solid ${theme.colors.border}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                color: theme.colors.textSecondary,
                fontSize: theme.fonts.sizeSmall,
                fontFamily: theme.fonts.body
              }}>
                <span>📍</span>
                <span><strong>Página:</strong> {currentPage}</span>
              </div>
            </div>
          )}

          {/* Tipo de feedback */}
          <div style={{ marginBottom: theme.spacing.lg }}>
            <label htmlFor="feedback-type" style={{
              display: 'block',
              color: theme.colors.text,
              fontFamily: theme.fonts.body,
              fontWeight: 'bold',
              marginBottom: theme.spacing.sm
            }}>
              Tipo de reporte *
            </label>
            <select
              id="feedback-type"
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              style={{
                width: '100%',
                padding: theme.spacing.sm,
                borderRadius: theme.borderRadius.input,
                border: `1px solid ${theme.colors.border}`,
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody
              }}
            >
              <option value="bug">🐛 Bug/Error</option>
              <option value="feature">💡 Nueva funcionalidad</option>
              <option value="improvement">⚡ Mejora</option>
              <option value="other">❓ Otro</option>
            </select>
          </div>

          {/* Descripción */}
          <div style={{ marginBottom: theme.spacing.lg }}>
            <label htmlFor="feedback-description" style={{
              display: 'block',
              color: theme.colors.text,
              fontFamily: theme.fonts.body,
              fontWeight: 'bold',
              marginBottom: theme.spacing.sm
            }}>
              Descripción *
            </label>
            <textarea
              id="feedback-description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe el problema, sugerencia o mejora que quieres reportar..."
              rows={6}
              style={{
                width: '100%',
                padding: theme.spacing.sm,
                borderRadius: theme.borderRadius.input,
                border: `1px solid ${errors.description ? theme.colors.danger : theme.colors.border}`,
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody,
                resize: 'vertical'
              }}
            />
            {errors.description && (
              <p style={{ color: theme.colors.danger, fontSize: '12px', margin: '4px 0 0 0' }}>
                {errors.description}
              </p>
            )}
          </div>

          {/* Error de envío */}
          {errors.submit && (
            <div style={{
              backgroundColor: theme.colors.danger + '20',
              border: `1px solid ${theme.colors.danger}`,
              borderRadius: theme.borderRadius.input,
              padding: theme.spacing.sm,
              marginBottom: theme.spacing.md,
              color: theme.colors.danger,
              fontFamily: theme.fonts.body
            }}>
              {errors.submit}
            </div>
          )}

          {/* Botones */}
          <div style={{
            display: 'flex',
            gap: theme.spacing.sm,
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                borderRadius: theme.borderRadius.button,
                border: `1px solid ${theme.colors.border}`,
                backgroundColor: 'transparent',
                color: theme.colors.textSecondary,
                fontFamily: theme.fonts.body,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                borderRadius: theme.borderRadius.button,
                border: 'none',
                backgroundColor: isSubmitting ? theme.colors.textSecondary : '#FF6B35',
                color: theme.colors.white,
                fontFamily: theme.fonts.body,
                fontWeight: 'bold',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar Reporte'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
